<template>
  <div ref="parentContainer" class="parent">
    <div
      v-for="item in items"
      :key="item.id"
      class="chart-container"
      :style="{ height: childHeight + 'px'}"
    >
      <VChart
        :ref="el => registerChartRef(item.id, el)"
        :option="item.option"
        :update-options="{ notMerge: false }"
        autoresize
        style="width: 100%; height: 100%"
        @click="onBarClick(item, $event)"
      />
    </div>
     <div v-if="items.length === 0" class="empty-state">
      <div class="empty-state-content">
        <svg class="bi" width="48" height="48" fill="currentColor">
          <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#graph-up"/>
        </svg>
        <p>No charts available. Configure measuring parameters and press Apply on the Map page.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { registerCapture, unregisterCapture, setCachedCapture } from '../../report/captureRegistry';
import { use, init as echartsInit } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DataZoomComponent
} from 'echarts/components';
import VChart from 'vue-echarts';
import { inject, reactive } from 'vue';
import { mapGetters } from 'vuex';

use([
  CanvasRenderer,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DataZoomComponent
]);

export default {
  name: 'EChartsComponent',
  components: { VChart },
  setup() {
    const newChart = inject('newChart');
    const isLive = inject('isLive');
    const isOffcanvas = inject('isOffcanvas');
    const filterChart = inject('filterChart');
    const colorChart = inject('colorChart', null);
    const selectedPoint = inject('selectedPoint', null);

    return {
      newChart,
      isLive,
      isOffcanvas,
      filterChart,
      colorChart,
      selectedPoint,
    };
  },
  data() {
    return {
      items: [],
      childHeight: 400,
      liveIntervals: {},
      chartFilters: reactive({}),
      chartRefs: {},
      lastHighlight: {},
    };
  },
  computed: {
    ...mapGetters('chartsParams', ['chartsParameters', 'chartsAppliedAt', 'chartsAppliedStartTime', 'chartsLogScale']),
     avgTime: {
      get () { return this.$store.getters.stateAvgTime},
      set (value) { this.setAvgTimeState(value)}
    },
      intervalTime: {
      get () { return this.$store.getters.stateIntervalTime},
      set (value) { this.setIntervalTimeState(value)}
    }
  },
  watch: {
    newChart: {
      handler(newV) {
        if (newV && newV.data) {
          this.addChart(newV);
        } else if (newV && newV.id !== undefined && newV.data === null) {
          this.removeChart(newV.id);
        }
      },
      deep: true
    },
    filterChart: {
      handler(newFilter) {
        if (newFilter && newFilter.id) {
          this.chartFilters[newFilter.id] = newFilter;
          this.applyFilterToChart(newFilter);
        }
      },
      deep: true
    },
    chartsAppliedAt: {
      immediate: true,
      handler() {
        this.syncFromStore();
      }
    },
    colorChart: {
      handler(newV) {
        if (newV && newV.id != null && newV.color) {
          this.updateItemColor(newV.id, newV.color);
        }
      },
      deep: true
    },
    selectedPoint: {
      deep: true,
      handler(newV) {
        if (!newV || !newV.timestamp) return;
        this.highlightTimestamp(newV.timestamp, newV.results);
      }
    },
    // Включение/выключение live-режима запускает или глушит опрос графиков.
    isLive() {
      this.refreshLiveUpdates();
    }
  },
  mounted() {
    this.calculateHeight();
    window.addEventListener('resize', this.calculateHeight);
    this.syncFromStore();
    registerCapture('charts', () => this.captureChartImages());
  },
  activated() {
    registerCapture('charts', () => this.captureChartImages());
    this.$nextTick(() => {
      this.syncFromStore();
      this.calculateHeight();
      window.dispatchEvent(new Event('resize'));
      // Re-pin tooltips after the tab becomes visible — ECharts cannot
      // compute correct positions while the chart container is hidden.
      this.$nextTick(() => this.replayHighlights());
    });
  },
  deactivated() {
    // Запасной снимок для отчёта на случай пустого живого захвата.
    setCachedCapture('charts', this.captureChartImages());
    // Вкладка Charts скрыта (keep-alive) — глушим live-опрос, чтобы не гонять
    // сеть в фоне; при возврате activated -> syncFromStore перезапустит его.
    this.clearAllIntervals();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateHeight);
    unregisterCapture('charts');
    this.clearAllIntervals();
  },
  methods: {
    // Снимки всех графиков Charts для Print Report. Не снимаем с экранных
    // canvas: при нескольких графиках их высота на экране сжимается
    // (childHeight = контейнер / число графиков), и в PDF они выходят
    // расплющенными. Вместо этого каждый график рендерится офскрин во
    // временный ECharts-инстанс фиксированного размера 1000x400 (пропорции
    // под ширину A4) с отключённой анимацией.
    captureChartImages() {
      const out = [];
      this.items.forEach((item) => {
        let chart = null;
        try {
          const holder = document.createElement('div');
          chart = echartsInit(holder, null, { width: 1000, height: 400 });
          // Прячем закреплённый tooltip — иначе он впечатается в снимок.
          chart.setOption({ ...item.option, animation: false, tooltip: { show: false } });
          out.push({
            title: (item.option && item.option.title && item.option.title.text) || item.type || '',
            dataUrl: chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' }),
          });
        } catch (e) {
          console.error('Chart capture failed:', e);
        } finally {
          if (chart) chart.dispose();
        }
      });
      return out;
    },
    // Десятичный логарифм значений (то же преобразование, что apply_scale('log')
    // в idw_processor.py): при наличии неположительных значений данные
    // сдвигаются так, чтобы минимум стал 1 — log10(v - min + 1), иначе log10(v).
    // shift считается по refValues (полному набору точек), чтобы фильтр
    // Data limits не менял лог-значение оставшихся точек.
    applyLogScale(values, refValues, logScale) {
      if (!logScale || !Array.isArray(values) || !values.length) return values;
      const ref = (Array.isArray(refValues) && refValues.length) ? refValues : values;
      let min = Infinity;
      for (const v of ref) {
        if (typeof v === 'number' && Number.isFinite(v) && v < min) min = v;
      }
      if (!Number.isFinite(min)) return values;
      const shift = min <= 0 ? 1 - min : 0;
      return values.map(v =>
        (typeof v === 'number' && Number.isFinite(v)) ? Math.log10(v + shift) : v
      );
    },
    // Подпись графика с пометкой «(log)», как у палитры на вкладке Map.
    withLogLabel(text, logScale) {
      return logScale ? `${text} (log)` : text;
    },
    calculateHeight() {
      if (!this.$refs.parentContainer) return;
      const parentHeight = this.$refs.parentContainer.clientHeight;
      const numItems = this.items.length || 1;
      this.childHeight = Math.max(100, Math.floor(parentHeight / numItems));
    },
    formatDate(date) {
      if (typeof date === 'string') {
        date = new Date(date);
      }

      const month = (date.getUTCMonth() + 1);
      const dateLabel = date.getUTCDate() < 10 ? '0' + date.getUTCDate().toString() : date.getUTCDate();
      const monthLabel = month < 10 ? '0' + month : month;
      const yearLabel = date.getUTCFullYear().toString().slice(-2);
      const hoursLabel = date.getUTCHours() < 10 ? '0' + date.getUTCHours().toString() : date.getUTCHours();
      const minutesLabel = date.getUTCMinutes() < 10 ? '0' + date.getUTCMinutes().toString() : date.getUTCMinutes();
      const secondsLabel = date.getSeconds() < 10 ? '0' + date.getSeconds().toString() : date.getSeconds();
      const millisecondsLabel = date.getUTCMilliseconds().toString().padStart(3, '0');

      return dateLabel + '/' + monthLabel + '/' + yearLabel + ',' +
             hoursLabel + ':' + minutesLabel + ':' + secondsLabel + '.' + millisecondsLabel;
    },
    formatLabels(labels) {
      return labels.map(label => this.formatDate(label));
    },
    replayHighlights() {
      Object.keys(this.lastHighlight).forEach(itemId => {
        const idx = this.lastHighlight[itemId];
        const chartEl = this.chartRefs[itemId];
        if (!chartEl || idx === undefined) return;
        try {
          chartEl.resize();
          chartEl.dispatchAction({ type: 'select', seriesIndex: 0, dataIndex: idx });
          chartEl.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: idx });
        } catch (e) { /* chart may not be ready */ }
      });
    },
    registerChartRef(id, el) {
      if (el) {
        this.chartRefs[id] = el;
      } else {
        delete this.chartRefs[id];
      }
    },
    onBarClick(item, params) {
      if (!params || typeof params.dataIndex !== 'number') return;
      const ts = item.originalLabels && item.originalLabels[params.dataIndex];
      if (!ts || !this.selectedPoint) return;
      this.selectedPoint.timestamp = ts;
      this.selectedPoint.source = 'chart';
      this.selectedPoint.results = null;
      this.selectedPoint.ts = Date.now();
    },
    findNearestIndexByTime(originalLabels, targetTimestamp) {
      const target = new Date(targetTimestamp).getTime();
      if (Number.isNaN(target) || !originalLabels || !originalLabels.length) return -1;
      let best = -1;
      let minDelta = Infinity;
      for (let i = 0; i < originalLabels.length; i += 1) {
        const t = new Date(originalLabels[i]).getTime();
        if (Number.isNaN(t)) continue;
        const delta = Math.abs(t - target);
        if (delta < minDelta) {
          minDelta = delta;
          best = i;
        }
      }
      return best;
    },
    highlightTimestamp(timestamp, results) {
      this.items.forEach(item => {
        if (!item.fromStore) return;
        const chartEl = this.chartRefs[item.id];
        if (!chartEl) return;

        const channelId = item.id.replace(/^param-/, '');
        const skip = results && (results[channelId] === null || results[channelId] === undefined);

        const prev = this.lastHighlight[item.id];
        if (prev !== undefined) {
          try {
            chartEl.dispatchAction({ type: 'unselect', seriesIndex: 0, dataIndex: prev });
          } catch (e) { /* chart may not be ready */ }
        }
        if (skip) {
          try { chartEl.dispatchAction({ type: 'hideTip' }); } catch (e) { /* */ }
          delete this.lastHighlight[item.id];
          return;
        }

        const idx = this.findNearestIndexByTime(item.originalLabels, timestamp);
        if (idx < 0) {
          delete this.lastHighlight[item.id];
          return;
        }

        try {
          chartEl.dispatchAction({ type: 'select', seriesIndex: 0, dataIndex: idx });
          chartEl.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: idx });
        } catch (e) { /* chart may not be ready */ }
        this.lastHighlight[item.id] = idx;
      });
    },
    updateItemColor(id, color) {
      const item = this.items.find(i => i.id === id);
      if (!item) return;
      item.color = color;
      item.option.series[0].itemStyle = { color };
      item.option.series[0].lineStyle = { color };
    },
    syncFromStore() {
      const params = this.chartsParameters || [];
      const enabled = params.filter(p => p.enabled);

      // Drop store-driven items whose params are no longer enabled.
      const enabledIds = new Set(enabled.map(p => this.storeItemId(p.id)));
      this.items = this.items.filter(item => !item.fromStore || enabledIds.has(item.id));

      // Upsert each enabled parameter as its own bar chart and apply filter.
      enabled.forEach(param => {
        this.upsertStoreItem(param);
        const filter = {
          id: this.storeItemId(param.id),
          is_filter: param.is_filter,
          filter_min: param.filter_min,
          filter_max: param.filter_max,
        };
        // Запоминаем фильтр, чтобы live-опрос применял Data limits к каждому
        // новому кадру (иначе продление графика игнорировало бы фильтр).
        this.chartFilters[filter.id] = filter;
        this.applyFilterToChart(filter);
      });
      this.calculateHeight();
      // (Пере)запускаем live-опрос под текущий набор графиков: в live-режиме
      // store-графики сами по себе статичны — их продлевает только этот опрос.
      this.refreshLiveUpdates();
    },
    // Синхронизирует набор live-интервалов с текущими store-графиками:
    // в не-live режиме глушит всё, в live — держит по одному интервалу на
    // активный график и снимает интервалы у исчезнувших.
    refreshLiveUpdates() {
      if (!this.isLive) {
        this.clearAllIntervals();
        return;
      }
      const liveItems = this.items.filter(item => item.fromStore);
      const liveIds = new Set(liveItems.map(item => item.id));
      Object.keys(this.liveIntervals).forEach(id => {
        if (!liveIds.has(id)) {
          clearInterval(this.liveIntervals[id]);
          delete this.liveIntervals[id];
        }
      });
      liveItems.forEach(item => {
        if (!this.liveIntervals[item.id]) {
          this.startLiveUpdates(item);
        }
      });
    },
    storeItemId(paramId) {
      return `param-${paramId}`;
    },
    upsertStoreItem(param) {
      const id = this.storeItemId(param.id);
      const data = Array.isArray(param.data) ? param.data : [];
      const labels = Array.isArray(param.labels) ? param.labels : [];
      const baseTitle = param.label || param.title || `Parameter ${param.id}`;
      const logScale = !!this.chartsLogScale;
      const title = this.withLogLabel(baseTitle, logScale);

      const existingIndex = this.items.findIndex(item => item.id === id);
      if (existingIndex !== -1) {
        const existing = this.items[existingIndex];
        existing.option.title.text = title;
        existing.option.xAxis.data = this.formatLabels(labels);
        existing.option.series[0].name = title;
        existing.option.series[0].data = this.applyLogScale(data, data, logScale);
        existing.option.series[0].itemStyle = { color: param.color };
        existing.option.series[0].lineStyle = { color: param.color };
        existing.option.series[0].showSymbol = data.length < 100;
        existing.color = param.color;
        existing.type = title;
        existing.logScale = logScale;
        // Формула и калибровка нужны для live-опроса /measurements/latest —
        // без них продление графика в live-режиме уходит с неверным param.
        existing.formula = param.formula || '';
        existing.calibrationId = param.calibrationId || '';
        // Left-край live-опроса фиксируем на момент Apply — чтобы позднейший
        // Add chart (двигающий глобальный dateStart) не сбивал окно графика.
        existing.startTime = this.chartsAppliedStartTime || existing.startTime || null;
        existing.originalData = [...data];
        existing.originalLabels = [...labels];
        return;
      }

      this.items.unshift({
        id,
        type: title,
        color: param.color,
        fromStore: true,
        formula: param.formula || '',
        calibrationId: param.calibrationId || '',
        // Собственный left-край live-опроса авто-графика (см. updateLiveData).
        startTime: this.chartsAppliedStartTime || null,
        logScale,
        option: {
          title: {
            text: title,
            left: 'center',
            textStyle: { fontSize: 12 }
          },
          tooltip: {
            trigger: 'item',
            triggerOn: 'none',
            alwaysShowContent: true,
            confine: true,
            position: 'top',
            axisPointer: { type: 'cross' }
          },
          grid: {
            left: '5%',
            right: '3%',
            bottom: '1%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: this.formatLabels(labels),
          },
          yAxis: { type: 'value' },
          series: [{
            name: title,
            type: 'bar',
            data: this.applyLogScale(data, data, logScale),
            itemStyle: { color: param.color },
            lineStyle: { color: param.color },
            smooth: true,
            showSymbol: data.length < 100,
            selectedMode: 'single',
            emphasis: { disabled: true },
            select: {
              itemStyle: {
                borderColor: '#000',
                borderWidth: 2,
                opacity: 1,
              }
            }
          }]
        },
        originalData: [...data],
        originalLabels: [...labels]
      });
    },
    applyFilterToChart(filter) {
      const chartIndex = this.items.findIndex(item => item.id === filter.id);
      if (chartIndex === -1) return;

      const chart = this.items[chartIndex];

      if (!filter.is_filter) {
        chart.option.xAxis.data = this.formatLabels(chart.originalLabels);
        chart.option.series[0].data = this.applyLogScale(
          chart.originalData, chart.originalData, chart.logScale
        );
      } else {
        const filteredData = [];
        const filteredLabels = [];
        chart.originalData.forEach((value, index) => {
          let passesFilter = true;

          if (filter.filter_min !== null && filter.filter_min !== undefined && filter.filter_min !== '')  {
            passesFilter = passesFilter && (value >= filter.filter_min);
          }

          if (filter.filter_max !== null && filter.filter_max !== undefined && filter.filter_max !== '') {
            passesFilter = passesFilter && (value <= filter.filter_max);
          }

          if (passesFilter) {
            filteredData.push(value);
            filteredLabels.push(chart.originalLabels[index]);
          }
        });
        chart.option.xAxis.data = this.formatLabels(filteredLabels);
        chart.option.series[0].data = this.applyLogScale(
          filteredData, chart.originalData, chart.logScale
        );
      }
    },
    addChart(chart) {
      const existingIndex = this.items.findIndex(item => item.id === chart.id);
      if (existingIndex !== -1) {
        this.updateChart(chart);
        return;
      }
      const logScale = !!chart.logScale;
      const chartTitle = this.withLogLabel(chart.type, logScale);
      const newItem = {
        id: chart.id,
        type: chart.type,
        color: chart.color,
        // Формула/калибровка — для live-опроса (см. updateLiveData). У legacy
        // add-chart формула лежит в chart.formula; fallback на type сохранён.
        formula: chart.formula || '',
        calibrationId: chart.calibrationId || '',
        // Собственный left-край live-опроса ручного графика — фиксируется в
        // момент Add chart и не зависит от последующих действий с другими графиками.
        startTime: chart.startTime || null,
        option: {
          title: {
            text: chartTitle,
            left: 'center',
            textStyle: {
              fontSize: 12
            }
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            }
          },
          grid: {
            left: '5%',
            right: '3%',
            bottom: '1%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: this.formatLabels(chart.labels),
          },
          yAxis: { type: 'value' },
          series: [{
            name: chartTitle,
            type: 'bar',
            data: this.applyLogScale(chart.data, chart.data, logScale),
            itemStyle: { color: chart.color },
            lineStyle: {
              color: chart.color
            },
            smooth: true,
            showSymbol: chart.data.length < 100
          }]
        },
        logScale,
        originalData: [...chart.data],
        originalLabels: [...chart.labels]
      };

      this.items.unshift(newItem);
      this.calculateHeight();

      if (this.isLive) {
        this.startLiveUpdates(newItem);
      }
      if (chart.is_filter) {
        this.chartFilters[chart.id] = chart;
        this.applyFilterToChart(chart);
      }
    },
    updateChart(chart) {
      const index = this.items.findIndex(item => item.id === chart.id);
      if (index === -1) return;

      const item = this.items[index];
      item.option.xAxis.data = this.formatLabels(chart.labels);
      item.option.series[0].data = this.applyLogScale(chart.data, chart.data, item.logScale);
      item.originalData = [...chart.data];
      item.originalLabels = [...chart.labels];
      if (chart.color) {
        item.color = chart.color;
        item.option.series[0].itemStyle = { color: chart.color };
        item.option.series[0].lineStyle = { color: chart.color };
      }
    },
    startLiveUpdates(item) {
      if (this.liveIntervals[item.id]) {
        clearInterval(this.liveIntervals[item.id]);
      }

      // Регистрируем интервал СИНХРОННО (до первого await в updateLiveData),
      // иначе два быстрых refreshLiveUpdates (syncFromStore + watcher isLive)
      // успели бы стартовать по второму интервалу на тот же график.
      // "Interval, s" задаёт частоту опроса. 0 -> опрашиваем раз в секунду.
      const refreshMs = (Number(this.intervalTime) > 0 ? Number(this.intervalTime) : 1) * 1000;
      this.liveIntervals[item.id] = setInterval(() => {
        this.updateLiveData(item);
      }, refreshMs);

      this.updateLiveData(item);
    },
    async updateLiveData(item) {
      try {
        // Левый край графика фиксируется при его создании (item.startTime) и не
        // зависит от последующих действий с другими графиками — иначе Add chart,
        // сдвигающий глобальный dateStart на «сейчас», обнулял бы окно уже
        // построенных графиков. Fallback на прежнюю глобальную логику — для
        // обратной совместимости со старыми item без поля startTime.
        const startTime = item.startTime != null
          ? item.startTime
          : (this.$store.getters.stateIsCustomData
            ? this.$store.getters.stateDateStart
            : this.$store.getters.stateCompleteDateStart);
        // store-графики опрашиваем по формуле параметра; для legacy-графиков
        // формулы нет — fallback на type (прежнее поведение).
        const params = {
          param: item.formula || item.type,
          interval: this.intervalTime,
          avg_time: this.avgTime,
          start_time: startTime,
        };
        // Калибровочный полином применяется на бэкенде по выбранной кривой.
        if (item.calibrationId) params.calibration_id = item.calibrationId;
        // Фильтр по типу лазера ("Laser, nm"); пустое значение не шлём.
        const laser = this.$store.getters.stateLaser;
        if (laser) params.laser = laser;

        const response = await axios.get('measurements/latest', { params });
        // Работаем с актуальным объектом из items (ссылка могла обновиться
        // через syncFromStore/upsertStoreItem), чтобы не писать в устаревший.
        const chart = this.items.find(i => i.id === item.id);
        if (!chart) return;

        chart.originalData = [...response.data.values];
        chart.originalLabels = [...response.data.times];

        const chartFilter = this.chartFilters[chart.id];
        if (chartFilter && chartFilter.is_filter) {
          this.applyFilterToChart(chartFilter);
        } else {
          chart.option.xAxis.data = this.formatLabels(chart.originalLabels);
          chart.option.series[0].data = this.applyLogScale(
            chart.originalData, chart.originalData, chart.logScale
          );
          chart.option.series[0].showSymbol = chart.originalData.length < 100;
        }
      } catch (error) {
        console.error('Error updating live data:', error);
      }
    },
    removeChart(chartId) {
      if (this.liveIntervals[chartId]) {
        clearInterval(this.liveIntervals[chartId]);
        delete this.liveIntervals[chartId];
      }

      if (this.chartFilters[chartId]) {
        delete this.chartFilters[chartId];
      }

      this.items = this.items.filter(item => item.id !== chartId);
      this.calculateHeight();
    },
    clearAllIntervals() {
      Object.values(this.liveIntervals).forEach(interval => {
        clearInterval(interval);
      });
      this.liveIntervals = {};
    }
  }
};
</script>

<style scoped>
.parent {
  height: 100vh;
  overflow-y: hidden;
  transition: margin-left 0.3s ease;
}

.chart-container {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px;
  background: white;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6c757d;
}

.empty-state-content {
  text-align: center;
}

.empty-state-content svg {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state-content p {
  font-size: 1.1rem;
  margin: 0;
}
</style>
